import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LogisticsProvider = "cieda" | "yango" | "depot";
export type PricingTier = "internal" | "standard" | "premium";

export interface LogisticsClientConfig {
  readonly default_provider?: LogisticsProvider;
  readonly pricing_tier?: PricingTier;
  readonly cod_enabled?: boolean;
}

export interface LogisticsClient {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly contact_name: string | null;
  readonly contact_phone: string | null;
  readonly contact_email: string | null;
  readonly billing_address: string | null;
  readonly is_active: boolean;
  readonly webhook_url: string | null;
  readonly config: LogisticsClientConfig;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface LogisticsClientsListResponse {
  readonly clients: readonly LogisticsClient[];
}

export interface CreateLogisticsClientInput {
  readonly name: string;
  readonly slug: string;
  readonly contact_name?: string;
  readonly contact_phone?: string;
  readonly contact_email?: string;
  readonly billing_address?: string;
  readonly webhook_url?: string;
  readonly config?: LogisticsClientConfig;
}

export interface UpdateLogisticsClientInput {
  readonly name?: string;
  readonly slug?: string;
  readonly contact_name?: string | null;
  readonly contact_phone?: string | null;
  readonly contact_email?: string | null;
  readonly billing_address?: string | null;
  readonly webhook_url?: string | null;
  readonly is_active?: boolean;
  readonly config?: LogisticsClientConfig;
}

export interface GenerateApiKeyResponse {
  readonly raw_key: string;
  readonly key_prefix: string;
}

// ---------------------------------------------------------------------------
// COD (Cash on Delivery) types
// ---------------------------------------------------------------------------

export type CodStatus = "pending" | "verified" | "reconciled";

export interface CodDriver {
  readonly id: string;
  readonly name: string;
}

export interface CodCollection {
  readonly id: string;
  readonly client_id: string;
  readonly driver_id: string | null;
  readonly delivery_reference: string | null;
  readonly amount_fcfa: number;
  readonly status: CodStatus;
  readonly notes: string | null;
  readonly collected_at: string;
  readonly updated_at: string;
  readonly drivers: CodDriver | null;
}

export interface CodCollectionsResponse {
  readonly data: readonly CodCollection[];
  readonly meta: {
    readonly total: number;
    readonly total_amount_fcfa: number;
  };
}

export interface CodDriverSummary {
  readonly driver_id: string;
  readonly driver_name: string;
  readonly total_collected_fcfa: number;
  readonly count: number;
  readonly pending_count: number;
  readonly verified_count: number;
  readonly reconciled_count: number;
}

export interface CodSummaryResponse {
  readonly data: readonly CodDriverSummary[];
}

export interface CodCollectionsParams {
  readonly client_id: string;
  readonly date_from?: string;
  readonly date_to?: string;
  readonly driver_id?: string;
  readonly status?: CodStatus;
}

export interface UpdateCodStatusInput {
  readonly status: "verified" | "reconciled";
  readonly notes?: string;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

const BASE = "/api/admin/yoboulmako/clients";

// Dashboard KPI types (mirrors dashboard route response)
export interface PerClientMetric {
  readonly client_id: string;
  readonly client_name: string;
  readonly total: number;
  readonly delivered: number;
  readonly failed: number;
  readonly pending: number;
}

export interface YoboulmakoDashboardKPIs {
  readonly total_clients: number;
  readonly total_deliveries: number;
  readonly active_deliveries: number;
  readonly delivered_today: number;
  readonly failed_today: number;
  readonly avg_delivery_minutes: number;
  readonly on_time_rate: number;
  readonly revenue_fcfa: number;
  readonly per_client: readonly PerClientMetric[];
}

export const yoboulmakoApi = {
  clients: {
    list: (): Promise<LogisticsClientsListResponse> =>
      adminFetch<LogisticsClientsListResponse>(BASE),

    get: (id: string): Promise<LogisticsClient> =>
      adminFetch<LogisticsClient>(`${BASE}/${id}`),

    create: (input: CreateLogisticsClientInput): Promise<LogisticsClient> =>
      adminFetch<LogisticsClient>(BASE, {
        method: "POST",
        body: JSON.stringify(input),
      }),

    update: (
      id: string,
      input: UpdateLogisticsClientInput,
    ): Promise<LogisticsClient> =>
      adminFetch<LogisticsClient>(`${BASE}/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),

    deactivate: (id: string): Promise<{ success: boolean }> =>
      adminFetch<{ success: boolean }>(`${BASE}/${id}`, {
        method: "DELETE",
      }),

    generateApiKey: (id: string): Promise<GenerateApiKeyResponse> =>
      adminFetch<GenerateApiKeyResponse>(`${BASE}/${id}/api-key`, {
        method: "POST",
      }),
  },

  dashboard: {
    get: (clientId?: string): Promise<YoboulmakoDashboardKPIs> => {
      const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
      return adminFetch<YoboulmakoDashboardKPIs>(
        `/api/admin/yoboulmako/dashboard${qs}`,
      );
    },
  },

  cod: {
    list: (params: CodCollectionsParams): Promise<CodCollectionsResponse> => {
      const qs = new URLSearchParams({ client_id: params.client_id });
      if (params.date_from) qs.set("date_from", params.date_from);
      if (params.date_to) qs.set("date_to", params.date_to);
      if (params.driver_id) qs.set("driver_id", params.driver_id);
      if (params.status) qs.set("status", params.status);
      return adminFetch<CodCollectionsResponse>(
        `/api/admin/yoboulmako/cod?${qs.toString()}`,
      );
    },

    updateStatus: (
      id: string,
      input: UpdateCodStatusInput,
    ): Promise<CodCollection> =>
      adminFetch<CodCollection>(`/api/admin/yoboulmako/cod/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),

    summary: (
      clientId: string,
      params?: { date_from?: string; date_to?: string },
    ): Promise<CodSummaryResponse> => {
      const qs = new URLSearchParams({ client_id: clientId });
      if (params?.date_from) qs.set("date_from", params.date_from);
      if (params?.date_to) qs.set("date_to", params.date_to);
      return adminFetch<CodSummaryResponse>(
        `/api/admin/yoboulmako/cod/summary?${qs.toString()}`,
      );
    },
  },
} as const;
